import { useQueries, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import {
  DictionaryPageableResponseDto,
  DictionaryResponseDto,
  DictionaryTypeCalendarConfigurationDto,
  DictionaryTypeDto,
  PermissionDto,
} from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { useHasPermission } from '@/contexts/userContext/util/useHasPermission.util.ts';
import { useGetDictionaryCalendarConfigurations } from '@/views/processes/offerInstance/util/useGetDictionaryCalendarConfigurations.util.ts';

// ponytail: single-page fetch capped at 500 active entries per requested type; add
// server-side search-as-you-type if any dictionary ever outgrows one page.
const PAGE_SIZE = 500;

type Return = {
  configurationByType: Partial<Record<DictionaryTypeDto, DictionaryTypeCalendarConfigurationDto>>;
  inactiveDictionaryNamesById: Map<number, string>;
  isConfigurationError: boolean;
  isLoading: boolean;
  optionsByType: Partial<Record<DictionaryTypeDto, SelectSearchOptionModel<number>[]>>;
};

/**
 * Active entries and calendar configurations of the given dictionary types, read with dictionary read;
 * without it nothing is asked for and there are no options.
 */
export const useGetDictionaryOptionsByTypes = (
  types: DictionaryTypeDto[],
  dictionaryIds: number[] = [],
): Return => {
  const { dictionariesApi } = useApiClient();
  const canReadDictionaries = useHasPermission(PermissionDto.DictionaryRead);
  const uniqueTypes = canReadDictionaries ? Array.from(new Set(types)).sort() : [];
  const uniqueDictionaryIds = Array.from(new Set(dictionaryIds)).sort((a, b) => a - b);

  const { data: activeDictionaries, isLoading } = useQuery<
    DictionaryPageableResponseDto,
    AxiosError
  >({
    enabled: uniqueTypes.length > 0,
    queryFn: async () => {
      const { data } = await dictionariesApi.getDictionaries(0, PAGE_SIZE, {
        active: true,
        types: uniqueTypes,
      });
      return data;
    },
    // under ALL, so refetching all dictionaries covers it; BY_TYPES keeps it apart from the
    // per-type key of useGetActiveDictionaryElements
    queryKey: [QueryKeyEnum.DICTIONARIES.ALL, QueryKeyEnum.DICTIONARIES.BY_TYPES, ...uniqueTypes],
  });
  const {
    configurationByType,
    isError: isConfigurationError,
    isLoading: isLoadingConfigurations,
  } = useGetDictionaryCalendarConfigurations(uniqueTypes);
  const activeDictionaryIds = new Set(
    activeDictionaries?.items.map(item => item.dictionaryId) ?? [],
  );
  const dictionaryIdsToFetch = activeDictionaries
    ? uniqueDictionaryIds.filter(dictionaryId => !activeDictionaryIds.has(dictionaryId))
    : [];
  const dictionaryQueries = useQueries({
    queries: dictionaryIdsToFetch.map(dictionaryId => ({
      enabled: activeDictionaries !== undefined,
      queryFn: async (): Promise<DictionaryResponseDto> =>
        (await dictionariesApi.getDictionary(dictionaryId)).data,
      queryKey: [QueryKeyEnum.DICTIONARIES.DETAILS, dictionaryId],
    })),
  });

  const optionsByType: Partial<Record<DictionaryTypeDto, SelectSearchOptionModel<number>[]>> = {};
  const inactiveDictionaryNamesById = new Map<number, string>();

  (activeDictionaries?.items ?? []).forEach(item => {
    const bucket = optionsByType[item.type] ?? [];
    bucket.push({ label: item.name, value: item.dictionaryId });
    optionsByType[item.type] = bucket;
  });
  dictionaryQueries.forEach(({ data: dictionary }, index) => {
    if (dictionary && !dictionary.active) {
      const dictionaryId = dictionaryIdsToFetch[index];
      inactiveDictionaryNamesById.set(dictionaryId, dictionary.name);
    }
  });

  return {
    configurationByType,
    inactiveDictionaryNamesById,
    isConfigurationError,
    isLoading:
      isLoading || isLoadingConfigurations || dictionaryQueries.some(query => query.isLoading),
    optionsByType,
  };
};
